import { SignatureV4 } from "@smithy/signature-v4";
import { Sha256 } from "@aws-crypto/sha256-js";
import { CustomerCredentials } from "../ui/customerDataStore";

interface Headers {
  [key: string]: string;
}

/**
 * Method to create a signed request for AWS API Gateway
 * @param method GET/POST/PATCH/PUT/DELETE
 * @param endpoint URL of endpoint including query parameters
 * @param credentials AWS Credentials obtained from STS/Cognito
 * ```typescript
 * credentials: {
 *      accessKeyId: string;
 *      secretAccessKey: string;
 *      sessionToken?: string;
 *      expiration?: string;
 * }
 * ```
 * @param headers Optional Headers to include in the request (JSON object)
 * ```typescript
 * headers: {
 *  "content-type": "application/json";
 * }
 * ```
 * @param payload Optional payload to include in the request (JSON object)
 * @param region Optional AWS region of the API Gateway, default eu-west-1
 * @param service Optional AWS service name, default execute-api
 * @returns
 */
export const signApiRequest = async (
  method: string,
  endpoint: string,
  credentials: CustomerCredentials,
  headers: Headers | string = {},
  payload: Record<string, unknown> | null = null,
  region: string = "eu-west-1",
  service: string = "execute-api",
): Promise<{
  url: string;
  method: string;
  headers: Headers;
  data: string | null;
  withCredentials: boolean;
  endpoint: string;
  options: {
    method: string;
    headers: Headers;
    body: string | null;
    credentials: "include";
  };
}> => {
  const url = new URL(endpoint);
  let sortedQueryObject: Record<string, string> | undefined;
  let sortedQueryString: string | undefined;

  try {
    // Parse query parameters into an object and sort them alphabetically
    if (url.search?.length > 0) {
      const queryObject = url.search
        .replace("?", "")
        .split("&")
        .filter((param) => param.includes("="))
        .reduce<Record<string, string>>((acc, param) => {
          const [key, value] = param.split("=");
          if (key !== undefined && value !== undefined) {
            acc[key] = value;
          }
          return acc;
        }, {});

      sortedQueryObject = Object.keys(queryObject)
        .sort()
        .reduce<Record<string, string>>((acc, key) => {
          acc[key] = queryObject[key] ?? "";
          return acc;
        }, {});

      sortedQueryString = Object.keys(sortedQueryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(sortedQueryObject?.[key] ?? "")}`,
        )
        .join("&");

      // Update the URL with the sorted query string
      url.search = sortedQueryString;
    }

    // Parse headers into an object
    let headerObject: Headers;
    if (typeof headers === "string") {
      headerObject = headers
        .split("\n")
        .filter((header) => header.includes(":"))
        .reduce<Headers>((acc, header) => {
          const [key, value] = header.split(":");
          if (key !== undefined && value !== undefined) {
            acc[key.trim()] = value.trim();
          }
          return acc;
        }, {});
    } else {
      headerObject = headers;
    }

    // Add host to the header object
    headerObject.host = url.hostname;

    // Sort the header object alphabetically
    const sortedHeaderObject = Object.keys(headerObject)
      .sort()
      .reduce<Headers>((acc, key) => {
        acc[key] = headerObject[key] ?? "";
        return acc;
      }, {});

    // Prepare the request object
    const requestToSign = {
      method,
      protocol: url.protocol,
      hostname: url.hostname, // Add hostname property
      headers: sortedHeaderObject,
      path: url.pathname,
      body: payload ? JSON.stringify(payload) : "",
      ...(sortedQueryObject && { query: sortedQueryObject }),
    };

    // Create the SignatureV4 signer
    const signer = new SignatureV4({
      credentials: credentials,
      region,
      service,
      sha256: Sha256,
    });

    // Sign the request
    const signedRequest = await signer.sign(requestToSign);

    // Remove host from headers before returning
    delete signedRequest.headers.host;
    // Return Axios-compatible configuration
    return {
      url: url.toString(), // The full URL object including query parameters
      method: signedRequest.method, // The HTTP method
      headers: signedRequest.headers as Headers, // The signed headers
      data: signedRequest.body || null, // The signed body (use 'data' for Axios)
      withCredentials: true, // Include credentials for handling cookies
      endpoint: url.href, // The full URL including query parameters
      options: {
        method: signedRequest.method, // The HTTP method
        headers: signedRequest.headers as Headers, // The signed headers
        body: signedRequest.body || null, // The signed body
        credentials: "include", // Include credentials in the request to be able to handle http only cookies
      },
    };
  } catch (error) {
    //console.error("Error signing the request:", error);
    throw error;
  }
};
